"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, CircleCheck as CheckCircle2, Mail, Phone, ArrowRight, ShieldCheck, Clock, Sparkles, Loader as Loader2, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const valuePoints = [{
  title: "100% Direct Consultant Access",
  description: "You partner directly with me—Yassine Essaadi. No junior account managers, outsourced work, or agency hand-offs."
}, {
  title: "Custom Strategy, Not Templates",
  description: "I analyze your actual search landscape and technical hurdles to craft an actionable strategy tailored to your business goals."
}, {
  title: "Transparent & Measurable Impact",
  description: "Clear, honest deliverables and revenue-focused reporting without inflated vanity metrics or artificial claims."
}];
const quickStats = [{
  label: "Response Time",
  value: "Under 24h"
}, {
  label: "Audit Turnaround",
  value: "3 Business Days"
}, {
  label: "Consultation Duration",
  value: "30 Mins Free"
}];
function CTAFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-accent/20 text-accent flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground">
          Consultation Request Received!
        </h3>
        <p className="text-muted-foreground text-sm max-w-md">
          {successMessage || "Thank you for reaching out. I will review your website details and personally get back to you within 24 hours."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-border text-foreground hover:bg-muted">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Please enter your full name",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background border-input text-foreground focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please enter your email address",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@yourcompany.com" className="bg-background border-input text-foreground focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Please provide your website URL"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Website URL</FormLabel>
            <FormControl>
              <Input type="url" placeholder="https://yourcompany.com" className="bg-background border-input text-foreground focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="goals" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">
              Primary SEO Challenge or Goal (Optional)
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g. Organic drop, technical audit, international expansion..." className="bg-background border-input text-foreground focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold shadow-md transition-all py-6 text-base">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 h-5 w-5" />
            Processing Your Request...
          </> : <>
            Book My Free SEO Consultation
            <ArrowRight className="ml-2 h-5 w-5" />
          </>}
      </Button>

      <p className="text-xs text-center text-muted-foreground pt-2 flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
        No spam. 100% confidential website review. Response within 24 hours.
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcta" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs md:text-sm font-medium mb-4 border border-border">
            <Sparkles className="w-4 h-4 text-accent" />
            <span>Direct Consultant Expertise • No Agency Fluff</span>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground">
            Book a Free SEO Consultation
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Ready to discover the growth potential of your organic search traffic?
            Speak directly with me to review your website, technical foundation,
            and growth strategy.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-7 space-y-8">
            <div className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-sm relative overflow-hidden">
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="relative shrink-0">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-sizes" alt="Yassine Essaadi - SEO Consultant" loading="lazy" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-primary shadow-sm" />
                  <div className="absolute -bottom-1 -right-1 bg-accent text-accent-foreground p-1 rounded-full" title="Direct Consultant Access">
                    <UserCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-semibold tracking-wider uppercase text-primary">
                    Independent Expert Assurance
                  </span>
                  <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                    {"\"You work directly with me.\""}
                  </h3>
                  <p className="text-sm text-muted-foreground pt-1">
                    I am Yassine Essaadi, founder of YE Consultancy. When you request a consultation or SEO audit, I handle your project from day one.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-serif font-semibold text-foreground flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                What to expect from our initial session:
              </h4>

              <div className="grid gap-4">
                {valuePoints.map((point, i) => <div data-index={i} key={point.title} className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border/60 hover:border-primary/30 transition-colors">
                    <div className="p-2 rounded-md bg-primary/10 text-primary shrink-0 mt-0.5">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-foreground text-sm sm:text-base">
                        {point.title}
                      </h5>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                        {point.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 space-y-6 shadow-sm">
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center divide-x divide-border">
                {quickStats.map((stat, i) => <div data-index={i} key={stat.label} className="px-1 sm:px-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      {stat.label}
                    </p>
                    <p className="text-sm sm:text-base font-bold text-foreground mt-1">
                      {stat.value}
                    </p>
                  </div>)}
              </div>

              <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-full bg-secondary text-secondary-foreground shrink-0">
                    <Mail className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Prefer direct email?</p>
                    <a href="mailto:contact@ye-consultancy.com" className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                      contact@ye-consultancy.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-full bg-secondary text-secondary-foreground shrink-0">
                    <Phone className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Call direct</p>
                    <a href="tel:+447490984430" className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                      +44 7490984430
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:col-span-5">
            <Card className="bg-card border-border shadow-lg overflow-hidden">
              <CardHeader className="bg-secondary/40 border-b border-border pb-6">
                <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-1">
                  <SafeHydrate><Calendar className="w-4 h-4 text-accent" /></SafeHydrate>
                  <span>Free Initial Discovery</span>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-serif font-bold text-card-foreground">
                  Request Your Consultation
                </CardTitle>
                <CardDescription className="text-muted-foreground text-xs sm:text-sm">
                  Fill in your website details below to receive a direct response and scheduling link.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6">
                <FormProvider formId="consultation-cta-form" sectionName="consultationcta" defaultValues={{
                name: "",
                email: "",
                website: "",
                goals: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                website: {
                  required: true
                }
              }} submitText="Book My Free SEO Consultation" successMessage="Thank you! I have received your request and will respond personally within 24 hours.">
                  <CTAFormContent />
                </FormProvider>
              </CardContent>
            </Card>

            <div className="mt-6 text-center space-y-2">
              <p className="text-xs text-muted-foreground">
                Want to see proven results before booking?
              </p>
              <Button asChild variant="outline" className="w-full border-border hover:bg-muted text-foreground">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore Genuine Case Studies</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}